import React, { useState, useMemo, useRef, useEffect } from "react";
import { Sparkles, ArrowRight, CheckCircle2, Phone, RotateCcw, Eye, Brain, BookOpen, Baby } from "lucide-react";

// 20 preguntas agrupadas por categoría clínica
const QUESTIONS = [
  // Fatiga visual / pantallas
  { id: "q1", cat: "fatiga", text: "Siento fatiga visual o los ojos cansados al final del día" },
  { id: "q2", cat: "fatiga", text: "Tengo dolores de cabeza tras usar pantallas o leer" },
  { id: "q3", cat: "fatiga", text: "Noto los ojos secos, rojos o con picor" },
  { id: "q4", cat: "fatiga", text: "Me molesta la luz del sol o las luces brillantes (fotofobia)" },

  // Acomodación y enfoque
  { id: "q5", cat: "enfoque", text: "Veo borroso al pasar de cerca a lejos (o al revés)" },
  { id: "q6", cat: "enfoque", text: "Necesito acercarme o alejar el texto para enfocar bien" },
  { id: "q7", cat: "enfoque", text: "Me cuesta mantener el foco al leer más de unos minutos" },
  { id: "q8", cat: "enfoque", text: "Guiño los ojos o los entrecierro para ver mejor" },

  // Binocular / visión doble
  { id: "q9", cat: "binocular", text: "A veces veo doble o las letras se mueven" },
  { id: "q10", cat: "binocular", text: "Tapo o cierro un ojo al leer o hacer tareas de cerca" },
  { id: "q11", cat: "binocular", text: "Inclino la cabeza al leer, escribir o mirar la tele" },
  { id: "q12", cat: "binocular", text: "Tengo mala percepción de la profundidad (distancias, escaleras…)" },

  // Lectura / aprendizaje
  { id: "q13", cat: "lectura", text: "Pierdo la línea, me salto palabras o uso el dedo al leer" },
  { id: "q14", cat: "lectura", text: "Me cuesta concentrarme o recordar lo que acabo de leer" },
  { id: "q15", cat: "lectura", text: "Tengo bajo rendimiento escolar o laboral inexplicable" },

  // Conducción / vida diaria
  { id: "q16", cat: "vida", text: "Me cuesta conducir de noche o con poca luz" },
  { id: "q17", cat: "vida", text: "Me mareo en el coche, barco o con movimientos" },

  // Infantil
  { id: "q18", cat: "infantil", text: "Mi hijo/a invierte letras/números o escribe muy sucio" },
  { id: "q19", cat: "infantil", text: "Mi hijo/a se cansa rápido al leer o hacer deberes" },
  { id: "q20", cat: "infantil", text: "Mi hijo/a se acerca mucho a la tele, libros o móvil" },
];

const OPTIONS = [
  { value: "nunca", label: "Nunca", score: 0, color: "var(--sage-500)" },
  { value: "a_veces", label: "A veces", score: 1, color: "#c6a24a" },
  { value: "siempre", label: "Siempre", score: 2, color: "#b85c3a" },
];

const CAT_META = {
  fatiga: { label: "Fatiga visual", icon: Eye },
  enfoque: { label: "Enfoque", icon: Eye },
  binocular: { label: "Visión binocular", icon: Brain },
  lectura: { label: "Lectura y aprendizaje", icon: BookOpen },
  vida: { label: "Vida diaria", icon: Sparkles },
  infantil: { label: "En niños/as", icon: Baby },
};

const VisualCheckup = () => {
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const resultRef = useRef(null);

  const setAnswer = (qid, val) => setAnswers((a) => ({ ...a, [qid]: val }));

  // Métricas
  const answered = Object.keys(answers).length;
  const flagged = useMemo(
    () => Object.values(answers).filter((v) => v === "a_veces" || v === "siempre").length,
    [answers]
  );
  const score = useMemo(
    () =>
      Object.values(answers).reduce((sum, v) => {
        const opt = OPTIONS.find((o) => o.value === v);
        return sum + (opt ? opt.score : 0);
      }, 0),
    [answers]
  );
  const progress = Math.round((answered / QUESTIONS.length) * 100);
  const allAnswered = answered === QUESTIONS.length;

  // Diagnóstico basado en número de síntomas marcados (A veces + Siempre)
  const diagnosis = useMemo(() => {
    if (flagged === 0)
      return {
        level: "excellent",
        title: "¡Todo parece ir bien!",
        msg: "No has marcado ningún síntoma. Aún así, una revisión visual anual es la mejor manera de prevenir problemas futuros.",
        cta: "Ver servicios",
        color: "#6e7d4c",
      };
    if (flagged <= 4)
      return {
        level: "light",
        title: "Algún síntoma leve",
        msg: "Has marcado pocos síntomas y de forma ocasional. Puede ser pasajero, pero si persisten merece la pena una revisión.",
        cta: "Pedir revisión",
        color: "#8b9c5c",
      };
    if (flagged <= 9)
      return {
        level: "moderate",
        title: "Te recomendamos una valoración",
        msg: "Varios síntomas apuntan a que tu sistema visual podría estar sobrecargado o tener alguna disfunción sin diagnosticar. Una evaluación profesional te sacará de dudas.",
        cta: "Pedir cita",
        color: "#c6a24a",
      };
    return {
      level: "urgent",
      title: "Llámanos · podemos ayudarte",
      msg: `Has marcado ${flagged} síntomas que aparecen con frecuencia. Es muy probable que exista un problema visual que esté afectando tu calidad de vida (y que tenga solución con la terapia adecuada). No lo dejes pasar — llámanos y te orientamos sin compromiso.`,
      cta: "Llamar ahora",
      color: "#b85c3a",
    };
  }, [flagged]);

  const reset = () => {
    setAnswers({});
    setShowResult(false);
    const section = document.getElementById("autoevaluacion");
    if (section) window.scrollTo({ top: section.offsetTop - 80, behavior: "smooth" });
  };

  useEffect(() => {
    if (showResult && resultRef.current) {
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [showResult]);

  // Agrupar preguntas por categoría
  const grouped = useMemo(() => {
    const g = {};
    QUESTIONS.forEach((q) => {
      if (!g[q.cat]) g[q.cat] = [];
      g[q.cat].push(q);
    });
    return g;
  }, []);

  return (
    <section className="py-20 px-6 relative overflow-hidden" id="autoevaluacion" data-testid="visual-checkup">
      {/* Decorativos */}
      <div className="absolute top-10 left-10 font-display text-6xl animate-pulse" style={{ color: "var(--sage-200)", opacity: 0.4 }}>✦</div>
      <div className="absolute bottom-20 right-16 font-display text-5xl animate-pulse" style={{ color: "var(--sage-200)", opacity: 0.3, animationDelay: "1s" }}>✧</div>

      <div className="max-w-4xl mx-auto relative">
        {/* Header */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-sans-body text-sm font-semibold" style={{ background: "var(--terracotta, #c98466)", color: "var(--cream)", boxShadow: "0 10px 25px -10px rgba(201,132,102,0.5)" }}>
            <Sparkles size={16} /> Autoevaluación gratuita · 2 minutos
          </div>
        </div>

        <div className="text-center">
          <h2 className="section-title" style={{ fontSize: "clamp(2rem, 4.5vw, 3rem)" }}>
            ¿Necesitas <span className="handwritten">nuestra ayuda</span>?
          </h2>
          <p className="font-sans-body text-[color:var(--ink-soft)] mt-5 max-w-2xl mx-auto text-lg">
            Responde a estas 20 preguntas sobre tu visión (o la de tu hijo/a). Te diremos si es buena idea una valoración profesional.
          </p>
        </div>

        {/* Progreso pegajoso */}
        <div className="sticky top-24 z-10 mt-10 mb-10 rounded-2xl p-4 md:p-5 backdrop-blur-md" style={{ background: "rgba(250,247,238,0.94)", border: "2px solid var(--sage-200)", boxShadow: "0 10px 30px -15px rgba(54,66,32,0.2)" }} data-testid="checkup-progress">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="font-sans-body">
              <span className="font-semibold text-[color:var(--ink)]">{answered} / {QUESTIONS.length}</span>
              <span className="text-[color:var(--ink-soft)] text-sm"> respondidas</span>
            </div>
            <div className="flex-1 min-w-[150px] mx-3">
              <div className="h-3 rounded-full overflow-hidden" style={{ background: "var(--sage-100)" }}>
                <div className="h-full rounded-full transition-all duration-500 ease-out" style={{ width: `${progress}%`, background: "linear-gradient(90deg, var(--sage-400) 0%, var(--sage-600) 100%)" }} data-testid="checkup-bar"></div>
              </div>
            </div>
            {answered > 0 && (
              <button
                onClick={() => setShowResult(true)}
                className="btn-primary text-sm py-2 px-4"
                data-testid="checkup-see-result"
              >
                {allAnswered ? "Ver resultado" : `Ver resultado (${answered}/${QUESTIONS.length})`} <ArrowRight size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Preguntas agrupadas por categoría */}
        <div className="space-y-10" data-testid="checkup-questions">
          {Object.entries(grouped).map(([cat, questions]) => {
            const meta = CAT_META[cat];
            const Icon = meta.icon;
            return (
              <div key={cat} className="soft-card">
                <div className="flex items-center gap-3 mb-5">
                  <div className="h-10 w-10 rounded-full flex items-center justify-center" style={{ background: "var(--sage-50)" }}>
                    <Icon size={18} style={{ color: "var(--sage-600)" }} />
                  </div>
                  <h3 className="text-xl font-medium" style={{ color: "var(--ink)" }}>{meta.label}</h3>
                </div>

                <div className="space-y-4">
                  {questions.map((q, idx) => (
                    <div key={q.id} className="pb-4" style={{ borderBottom: idx < questions.length - 1 ? "1px solid var(--sage-100)" : "none" }}>
                      <div className="flex items-start gap-3 mb-3">
                        <span className="font-display text-sm mt-0.5 shrink-0" style={{ color: "var(--sage-500)" }}>✦</span>
                        <p className="font-sans-body text-[color:var(--ink)]">{q.text}</p>
                      </div>
                      <div className="grid grid-cols-3 gap-2 ml-6">
                        {OPTIONS.map((opt) => {
                          const active = answers[q.id] === opt.value;
                          return (
                            <button
                              key={opt.value}
                              onClick={() => setAnswer(q.id, opt.value)}
                              className="rounded-full py-2 px-3 font-sans-body text-sm font-medium transition-all"
                              style={{
                                background: active ? opt.color : "var(--cream)",
                                color: active ? "var(--cream)" : "var(--ink-soft)",
                                border: `1.5px solid ${active ? opt.color : "var(--sage-100)"}`,
                                transform: active ? "scale(1.03)" : "scale(1)",
                                boxShadow: active ? `0 6px 15px -5px ${opt.color}99` : "none",
                              }}
                              data-testid={`checkup-${q.id}-${opt.value}`}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA final cuando terminan */}
        {!showResult && answered > 0 && (
          <div className="mt-10 text-center">
            <button
              onClick={() => setShowResult(true)}
              className="btn-primary"
              data-testid="checkup-final-btn"
            >
              {allAnswered ? "Ver mi resultado" : `Ver resultado con ${answered} respuestas`} <ArrowRight size={18} />
            </button>
            {!allAnswered && (
              <p className="font-sans-body text-xs text-[color:var(--ink-soft)] mt-3">
                Te faltan {QUESTIONS.length - answered} preguntas · cuantas más respondas, más preciso será el resultado
              </p>
            )}
          </div>
        )}

        {/* RESULTADO */}
        {showResult && (
          <div
            ref={resultRef}
            className="mt-12 rounded-3xl p-8 md:p-10 fade-up"
            style={{
              background: "var(--sage-500)",
              color: "var(--cream)",
              boxShadow: "0 30px 60px -30px rgba(54,66,32,0.4)",
            }}
            data-testid="checkup-result"
          >
            {/* Score summary arriba */}
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="rounded-xl px-4 py-3 flex-1 min-w-[140px]" style={{ background: "rgba(250,247,238,0.12)" }}>
                <div className="text-xs uppercase tracking-widest opacity-80">Síntomas presentes</div>
                <div className="font-display text-3xl mt-1">{flagged} <span className="text-lg opacity-70">/ {QUESTIONS.length}</span></div>
              </div>
              <div className="rounded-xl px-4 py-3 flex-1 min-w-[140px]" style={{ background: "rgba(250,247,238,0.12)" }}>
                <div className="text-xs uppercase tracking-widest opacity-80">Puntuación</div>
                <div className="font-display text-3xl mt-1">{score} <span className="text-lg opacity-70">/ {QUESTIONS.length * 2}</span></div>
              </div>
              <div className="rounded-xl px-4 py-3 flex-1 min-w-[140px]" style={{ background: "rgba(250,247,238,0.12)" }}>
                <div className="text-xs uppercase tracking-widest opacity-80">Preguntas respondidas</div>
                <div className="font-display text-3xl mt-1">{answered} <span className="text-lg opacity-70">/ {QUESTIONS.length}</span></div>
              </div>
            </div>

            {/* Diagnóstico */}
            <div className="flex items-start gap-4">
              <div className="h-14 w-14 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--cream)" }}>
                {diagnosis.level === "excellent" || diagnosis.level === "light" ? (
                  <CheckCircle2 size={28} style={{ color: diagnosis.color }} />
                ) : (
                  <Phone size={24} style={{ color: diagnosis.color }} />
                )}
              </div>
              <div className="flex-1">
                <div className="section-eyebrow" style={{ color: "var(--sage-200)" }}>Tu valoración</div>
                <h3 className="text-2xl md:text-3xl font-medium mt-2">{diagnosis.title}</h3>
                <p className="font-sans-body mt-4 text-base opacity-95 leading-relaxed">{diagnosis.msg}</p>

                {/* Regla explicativa */}
                <div className="mt-6 p-4 rounded-xl font-sans-body text-sm" style={{ background: "rgba(250,247,238,0.12)" }}>
                  <strong>📝 Cómo interpretamos el resultado:</strong> si has marcado <strong>10 o más síntomas</strong> que aparecen "a veces" o "siempre", tu visión probablemente está comprometida y te recomendamos encarecidamente una valoración profesional. Esta autoevaluación es orientativa y no sustituye a un examen clínico.
                </div>

                {/* CTAs según nivel */}
                <div className="mt-8 flex flex-wrap gap-3">
                  {diagnosis.level === "urgent" && (
                    <a
                      href="tel:+34914044877"
                      className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold"
                      style={{ background: "var(--cream)", color: "var(--sage-700)", boxShadow: "0 8px 20px -8px rgba(0,0,0,0.3)" }}
                      data-testid="checkup-cta-call"
                    >
                      <Phone size={18} /> Llamar a 91 404 48 77
                    </a>
                  )}
                  <a
                    href="/#contacto"
                    className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-medium"
                    style={{
                      background: diagnosis.level === "urgent" ? "transparent" : "var(--cream)",
                      color: diagnosis.level === "urgent" ? "var(--cream)" : "var(--sage-700)",
                      border: diagnosis.level === "urgent" ? "1.5px solid var(--cream)" : "none",
                    }}
                    data-testid="checkup-cta-contact"
                  >
                    {diagnosis.cta} <ArrowRight size={18} />
                  </a>
                  <button
                    onClick={reset}
                    className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 font-medium"
                    style={{ border: "1.5px solid rgba(250,247,238,0.4)", color: "var(--cream)" }}
                    data-testid="checkup-cta-reset"
                  >
                    <RotateCcw size={16} /> Volver a empezar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default VisualCheckup;
